import { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles } from 'lucide-react';
import { useArbiterStore } from '../../stores/arbiterStore';
import { queryArbiter } from '../../arbiter';
import { cn } from '../../lib/utils';
import { LoadingSpinner } from '../common/LoadingSpinner';

export function ArbiterPanel() {
  const { panelOpen, messages, isProcessing, togglePanel, addMessage, setProcessing } =
    useArbiterStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;

    const userMessage = input.trim();
    setInput('');

    // Add user message
    addMessage({ type: 'user', content: userMessage });

    // Process query
    setProcessing(true);
    try {
      const response = await queryArbiter({ query: userMessage });
      addMessage({
        type: 'arbiter',
        content: response.content,
      });
    } catch (error) {
      addMessage({
        type: 'arbiter',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
      });
    } finally {
      setProcessing(false);
    }
  };

  if (!panelOpen) return null;

  return (
    <div className="fixed right-0 top-16 z-40 flex h-[calc(100vh-4rem)] w-[400px] flex-col border-l border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Arbiter
          </h2>
        </div>
        <button
          onClick={togglePanel}
          className="rounded p-1 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <Sparkles className="mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Ask Arbiter anything
            </p>
            <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
              Get insights on financial metrics, performance, and strategy
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={() => setInput('What is our cost to income ratio?')}
                className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-xs hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700"
              >
                Cost to income ratio
              </button>
              <button
                onClick={() => setInput('Tell me about our liquidity position')}
                className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-xs hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700"
              >
                Liquidity position
              </button>
              <button
                onClick={() => setInput('How is our capital ratio?')}
                className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-xs hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700"
              >
                Capital ratio
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex',
                  message.type === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    'max-w-[85%] rounded-lg px-4 py-2',
                    message.type === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-slate-100'
                  )}
                >
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="rounded-lg bg-slate-100 px-4 py-2 dark:bg-slate-700">
                  <LoadingSpinner size="sm" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-4 dark:border-slate-700">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question..."
            disabled={isProcessing}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <button
            type="submit"
            disabled={!input.trim() || isProcessing}
            className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
